import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';

type EditableSuggestedBidCellProps = {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  label?: string;
};

export function EditableSuggestedBidCell({
  value,
  onChange,
  className,
  label = 'Suggested bid',
}: EditableSuggestedBidCellProps) {
  const [draft, setDraft] = useState(String(value));
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) setDraft(String(value));
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const commit = () => {
    const parsed = Number(draft);
    if (Number.isFinite(parsed) && parsed > 0) {
      onChange(parsed);
      setIsEditing(false);
      return;
    }
    setDraft(String(value));
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="0.02"
        step="0.01"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
          if (event.key === 'Escape') {
            setDraft(String(value));
            setIsEditing(false);
          }
        }}
        aria-label={label}
        className={cn(
          'w-20 rounded border border-border bg-surface px-2 py-1 text-right font-mono text-sm tabular-nums',
          className
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className={cn(
        'font-mono text-sm tabular-nums text-foreground hover:text-brand hover:underline underline-offset-2',
        className
      )}
      aria-label={`${label}: ${value.toFixed(2)}. Click to edit.`}
    >
      {value.toFixed(2)}
    </button>
  );
}
